import React from "react";

import { motion } from "framer-motion";

import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebookF,
  FaInstagram,
  FaYoutube,
} from "react-icons/fa";

import schoolImg from "../assets/uploads/school.jpg";

export default function ContactPage() {
  return (
    <section className="bg-[#f7f9fc] min-h-screen overflow-hidden">

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">

        {/* ================= HERO ================= */}

        <div className="grid lg:grid-cols-2 gap-14 items-center">

          {/* ================= LEFT IMAGE ================= */}

          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative py-6"
          >

            {/* Decorative Gold Circle */}

            <div className="absolute -top-3 -left-5 w-24 h-24 border-[12px] border-[#d4af37]/20 rounded-full"></div>

            {/* Decorative Blue Shape */}

            <div className="absolute -bottom-7 -right-7 w-32 h-32 bg-[#07294d]/5 rounded-[30px] rotate-6"></div>


            {/* Main Image Frame */}

            <div className="relative bg-white p-3 rounded-[35px] shadow-[0_25px_70px_rgba(0,0,0,0.15)]">

              {/* Gold Border */}

              <div className="absolute inset-0 rounded-[35px] border-2 border-[#d4af37]/40 pointer-events-none"></div>


              {/* Image */}

              <div className="relative overflow-hidden rounded-[27px] bg-[#f3f6fa]">

                <img
                  src={schoolImg}
                  alt="Cambridge International School"
                  className="
                    w-full
                    h-[520px]
                    object-contain
                    block
                    transition
                    duration-700
                    hover:scale-[1.03]
                  "
                />

                {/* Subtle Bottom Gradient */}

                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#07294d]/25 to-transparent pointer-events-none"></div>

              </div>


              {/* Floating School Badge */}

              <motion.div
                animate={{ y: [0, -7, 0] }}
                transition={{
                  duration: 3.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute -bottom-6 -left-6 bg-white rounded-2xl px-5 py-4 shadow-xl border border-gray-100"
              >

                <p className="text-xs uppercase tracking-[3px] text-[#d4af37] font-bold">
                  Cambridge
                </p>

                <p className="text-xl font-black text-[#07294d]">
                  International School
                </p>

              </motion.div>

            </div>

          </motion.div>


          {/* ================= RIGHT CONTENT ================= */}

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >

            <span className="inline-block px-5 py-2 rounded-full bg-[#d4af37]/15 text-[#07294d] font-bold tracking-[3px] uppercase text-sm">

              Contact Us

            </span>


            <h1 className="text-5xl lg:text-6xl font-black text-[#07294d] leading-tight mt-6">

              We're Here

              <br />

              To Help You

            </h1>


            <div className="w-24 h-1 bg-[#d4af37] rounded-full my-8"></div>


            <p className="text-gray-600 text-lg leading-9 max-w-xl">

              Whether you're looking for admission information,
              transport details, fee assistance, or have general
              enquiries, our team is always ready to help you.

              Feel free to connect with Cambridge International School.

            </p>


            {/* QUICK INFO */}

            <div className="grid sm:grid-cols-3 gap-5 mt-12">

              <div className="bg-white rounded-2xl p-5 shadow-lg">

                <div className="w-12 h-12 rounded-xl bg-[#07294d] text-[#d4af37] flex items-center justify-center mb-4">

                  <FaPhoneAlt />

                </div>

                <h4 className="font-bold text-[#07294d]">
                  Call Us
                </h4>

              </div>


              <div className="bg-white rounded-2xl p-5 shadow-lg">

                <div className="w-12 h-12 rounded-xl bg-[#07294d] text-[#d4af37] flex items-center justify-center mb-4">

                  <FaEnvelope />

                </div>

                <h4 className="font-bold text-[#07294d]">
                  Email
                </h4>

              </div>


              <div className="bg-white rounded-2xl p-5 shadow-lg">

                <div className="w-12 h-12 rounded-xl bg-[#07294d] text-[#d4af37] flex items-center justify-center mb-4">

                  <FaMapMarkerAlt />

                </div>

                <h4 className="font-bold text-[#07294d]">
                  Visit
                </h4>

              </div>

            </div>

          </motion.div>

        </div>


        {/* ================= CONTACT SECTION ================= */}

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 mt-20">


          {/* ================= LEFT INFO ================= */}

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >

            <div className="bg-white rounded-[30px] shadow-xl p-8 h-full">

              <p className="uppercase tracking-[5px] text-[#d4af37] font-bold mb-3">
                Contact Information
              </p>

              <h2 className="text-4xl font-black text-[#07294d] mb-10">
                Get In Touch
              </h2>


              {/* PHONE */}

              <div className="flex gap-5 mb-8">

                <div className="w-14 h-14 rounded-xl bg-[#07294d] text-[#d4af37] flex items-center justify-center text-xl">

                  <FaPhoneAlt />

                </div>

                <div>

                  <p className="text-sm text-gray-500 uppercase">
                    Phone Number
                  </p>

                  <h4 className="text-xl font-bold text-[#07294d]">
                    +91 98765 43210
                  </h4>

                </div>

              </div>


              {/* EMAIL */}

              <div className="flex gap-5 mb-8">

                <div className="w-14 h-14 rounded-xl bg-[#07294d] text-[#d4af37] flex items-center justify-center text-xl">

                  <FaEnvelope />

                </div>

                <div>

                  <p className="text-sm text-gray-500 uppercase">
                    Email Address
                  </p>

                  <h4 className="text-xl font-bold text-[#07294d] break-all">
                    info@cambridgeschool.com
                  </h4>

                </div>

              </div>


              {/* ADDRESS */}

              <div className="flex gap-5">

                <div className="w-14 h-14 rounded-xl bg-[#07294d] text-[#d4af37] flex items-center justify-center text-xl">

                  <FaMapMarkerAlt />

                </div>

                <div>

                  <p className="text-sm text-gray-500 uppercase">
                    School Address
                  </p>

                  <h4 className="text-xl font-bold text-[#07294d]">
                    Nakodar, Punjab, India
                  </h4>

                </div>

              </div>


              <div className="border-t mt-10 pt-8">

                <p className="text-gray-600 leading-8">

                  Office Hours

                  <br />

                  <span className="font-semibold text-[#07294d]">
                    Monday - Saturday
                  </span>

                  <br />

                  8:00 AM – 4:00 PM

                </p>

              </div>

            </div>

          </motion.div>


          {/* ================= CONTACT FORM ================= */}

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >

            <div className="bg-white rounded-[30px] shadow-xl p-8">

              <p className="uppercase tracking-[5px] text-[#d4af37] font-bold mb-3">
                Send Message
              </p>

              <h2 className="text-4xl font-black text-[#07294d] mb-10">
                Contact Form
              </h2>


              <form className="space-y-6">

                <div className="grid md:grid-cols-2 gap-6">

                  <input
                    type="text"
                    placeholder="Full Name"
                    className="h-14 rounded-xl border border-gray-300 px-5 outline-none focus:border-[#07294d]"
                  />

                  <input
                    type="text"
                    placeholder="Phone Number"
                    className="h-14 rounded-xl border border-gray-300 px-5 outline-none focus:border-[#07294d]"
                  />

                </div>


                <input
                  type="email"
                  placeholder="Email Address"
                  className="w-full h-14 rounded-xl border border-gray-300 px-5 outline-none focus:border-[#07294d]"
                />


                <input
                  type="text"
                  placeholder="Subject"
                  className="w-full h-14 rounded-xl border border-gray-300 px-5 outline-none focus:border-[#07294d]"
                />


                <textarea
                  rows="6"
                  placeholder="Write your message..."
                  className="w-full rounded-xl border border-gray-300 p-5 resize-none outline-none focus:border-[#07294d]"
                ></textarea>


                <button
                  type="submit"
                  className="w-full h-14 rounded-xl bg-[#07294d] hover:bg-[#0b3d6d] text-white font-bold text-lg transition-all duration-300"
                >
                  Send Message
                </button>

              </form>

            </div>

          </motion.div>

        </div>


        {/* ================= LOCATION & HELP ================= */}

        <div className="grid lg:grid-cols-2 gap-10 mt-20">


          {/* ================= GOOGLE MAP ================= */}

          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >

            <div className="bg-white rounded-[30px] shadow-xl overflow-hidden">

              <div className="p-8">

                <p className="uppercase tracking-[5px] text-[#d4af37] font-bold">
                  Find Us
                </p>

                <h2 className="text-4xl font-black text-[#07294d] mt-3">
                  Visit Our Campus
                </h2>

                <p className="text-gray-600 mt-4 leading-8">
                  We warmly welcome parents and students to visit our
                  campus and experience our learning environment.
                </p>

              </div>


              <iframe
                title="Cambridge International School"
                src="https://www.google.com/maps?q=Cambridge+International+School+Nakodar&output=embed"
                className="w-full h-[420px] border-0"
                loading="lazy"
              ></iframe>

            </div>

          </motion.div>


          {/* ================= HELP BOX ================= */}

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >

            <div className="bg-[#07294d] rounded-[30px] p-10 text-white h-full flex flex-col justify-between">

              <div>

                <span className="uppercase tracking-[5px] text-[#d4af37] font-bold">
                  Admissions
                </span>

                <h2 className="text-4xl font-black mt-4 leading-tight">

                  Need Admission

                  <br />

                  Assistance?

                </h2>

                <p className="mt-6 text-gray-300 leading-8">

                  Our admission counsellors are available to guide you
                  regarding admission procedures, fee structure,
                  transport facilities and curriculum.

                </p>

              </div>


              <div className="space-y-5 mt-10">

                <div className="flex justify-between border-b border-white/20 pb-4">

                  <span>
                    Admission Enquiry
                  </span>

                  <span className="font-bold text-[#d4af37]">
                    Available
                  </span>

                </div>


                <div className="flex justify-between border-b border-white/20 pb-4">

                  <span>
                    Office Timing
                  </span>

                  <span className="font-bold">
                    8:00 AM - 4:00 PM
                  </span>

                </div>


                <div className="flex justify-between pb-2">

                  <span>
                    Response Time
                  </span>

                  <span className="font-bold text-[#d4af37]">
                    Within 24 Hours
                  </span>

                </div>

              </div>


              <div className="mt-12">

                <a
                  href="/admission"
                  className="inline-block bg-[#d4af37] text-[#07294d] px-8 py-4 rounded-xl font-bold hover:scale-105 transition duration-300"
                >
                  Apply for Admission
                </a>

              </div>

            </div>

          </motion.div>

        </div>


        {/* ================= BOTTOM CTA ================= */}

        <section className="mt-20">

          <div className="bg-[#07294d] rounded-[35px] overflow-hidden">

            <div className="grid lg:grid-cols-2">


              {/* LEFT */}

              <div className="p-10 lg:p-14">

                <p className="uppercase tracking-[6px] text-[#d4af37] font-bold">
                  School Timings
                </p>

                <h2 className="text-4xl font-black text-white mt-4">

                  Visit Cambridge

                  <br />

                  International School

                </h2>


                <div className="mt-10 space-y-5">

                  <div className="flex justify-between border-b border-white/10 pb-4">

                    <span className="text-gray-300">
                      Monday - Friday
                    </span>

                    <span className="text-white font-semibold">
                      8:00 AM – 4:00 PM
                    </span>

                  </div>


                  <div className="flex justify-between border-b border-white/10 pb-4">

                    <span className="text-gray-300">
                      Saturday
                    </span>

                    <span className="text-white font-semibold">
                      8:00 AM – 1:00 PM
                    </span>

                  </div>


                  <div className="flex justify-between">

                    <span className="text-gray-300">
                      Sunday
                    </span>

                    <span className="text-[#d4af37] font-bold">
                      Closed
                    </span>

                  </div>

                </div>

              </div>


              {/* RIGHT */}

              <div className="bg-[#0b355f] p-10 lg:p-14 flex flex-col justify-center">

                <h3 className="text-3xl font-black text-white">
                  Stay Connected
                </h3>

                <p className="text-gray-300 mt-4 leading-8">

                  Follow Cambridge International School for
                  latest updates, admissions, events and achievements.

                </p>


                <div className="flex gap-4 mt-10">

                  <a
                    href="https://www.facebook.com/cisnakodar/"
                    className="w-14 h-14 rounded-full bg-white text-[#07294d] flex items-center justify-center hover:bg-[#d4af37] transition"
                  >
                    <FaFacebookF />
                  </a>


                  <a
                    href="https://www.instagram.com/cisnakodar/"
                    className="w-14 h-14 rounded-full bg-white text-[#07294d] flex items-center justify-center hover:bg-[#d4af37] transition"
                  >
                    <FaInstagram />
                  </a>


                  <a
                    href="https://youtube.com/"
                    className="w-14 h-14 rounded-full bg-white text-[#07294d] flex items-center justify-center hover:bg-[#d4af37] transition"
                  >
                    <FaYoutube />
                  </a>

                </div>


            

              </div>

            </div>

          </div>

        </section>


        {/* ================= FOOTER ================= */}

        <div className="text-center py-10">

          <div className="w-20 h-1 bg-[#d4af37] rounded-full mx-auto mb-6"></div>

          <h3 className="text-2xl font-bold text-[#07294d]">
            Cambridge International School
          </h3>

          <p className="text-gray-500 mt-3">
            Excellence • Integrity • Innovation
          </p>

          <p className="text-gray-400 text-sm mt-6">

            © {new Date().getFullYear()} Cambridge International School.
            All Rights Reserved.

          </p>

        </div>

      </div>

    </section>
  );
}